import { requireAdmin } from "@/lib/admin-guard";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMembership } from "@/hooks/useMembership";
import {
  getStripeOpsSnapshot,
  resyncMembershipFromStripe,
  type StripeOpsSnapshot,
} from "@/lib/stripe-ops.functions";
import {
  createFoundingCheckoutSession,
  createCustomerPortalSession,
} from "@/lib/membership.functions";
import {
  Loader2,
  ShieldAlert,
  Search,
  RefreshCw,
  ExternalLink,
  CreditCard,
  Receipt,
  Webhook,
  Clock,
  User as UserIcon,
  Copy,
} from "lucide-react";

export const Route = createFileRoute("/admin_/stripe-ops")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Admin — Stripe Operations (Recovery Trauma™)" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminStripeOpsPage,
});

function fmtDate(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB");
}

function fmtMoney(cents: number, currency: string): string {
  return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

function CopyBtn({ value }: { value: string | null | undefined }) {
  if (!value) return null;
  return (
    <button
      onClick={() => navigator.clipboard.writeText(value)}
      className="ms-1 inline-flex text-muted-foreground hover:text-foreground"
      title="Copy"
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/40 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-end ${mono ? "font-mono text-xs" : ""}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function StatusPill({ value }: { value: string | null }) {
  if (!value) return <>—</>;
  const tone: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-900",
    trialing: "bg-emerald-100 text-emerald-900",
    pending_activation: "bg-amber-100 text-amber-900",
    past_due: "bg-red-100 text-red-900",
    payment_failed: "bg-red-100 text-red-900",
    unpaid: "bg-red-100 text-red-900",
    canceled: "bg-muted text-foreground",
    cancelled: "bg-muted text-foreground",
    incomplete: "bg-amber-100 text-amber-900",
    incomplete_expired: "bg-muted text-foreground",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${tone[value] ?? "bg-muted"}`}>
      {value}
    </span>
  );
}

function AdminStripeOpsPage() {
  const { loading: memLoading, isAdmin } = useMembership();
  const snapshot = useServerFn(getStripeOpsSnapshot);
  const resync = useServerFn(resyncMembershipFromStripe);
  const createCheckout = useServerFn(createFoundingCheckoutSession);
  const portal = useServerFn(createCustomerPortalSession);

  const [lookup, setLookup] = useState("");
  const [data, setData] = useState<StripeOpsSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const lastQueryRef = useRef<string>("");

  async function runLookup(q?: string) {
    const v = (q ?? lookup).trim();
    if (!v) return;
    lastQueryRef.current = v;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
      const res = await snapshot({
        data: isUuid ? { user_id: v } : { email: v },
      });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setBusy(false);
    }
  }

  // Auto-search: debounce input and trigger lookup for plausible email/uuid.
  useEffect(() => {
    const v = lookup.trim();
    if (!v) return;
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    if (!isUuid && !isEmail) return;
    if (lastQueryRef.current === v) return;
    const t = setTimeout(() => {
      runLookup(v);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup]);

  if (memLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-4xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
        <p className="mt-3 text-sm">Admin access required.</p>
        <Link to="/" className="mt-4 inline-block text-sm underline">
          Back to home
        </Link>
      </div>
    );
  }

  const m = data?.membership;
  const a = data?.auth_user;
  const s = data?.stripe;
  const w = data?.webhook;
  const dbg = data?.debug;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-2xl text-foreground">Stripe Operations</h1>
        <Link
          to="/admin/membership"
          className="text-xs text-muted-foreground underline"
        >
          ← Membership console
        </Link>
      </div>

      <div className="mb-6 rounded-2xl border border-border bg-card/60 p-4">
        <p className="mb-2 text-sm font-medium">Look up a member</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runLookup();
          }}
          className="flex flex-col gap-2 sm:flex-row"
        >
          <input
            type="text"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            placeholder="email or user_id (uuid)"
            className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={busy || !lookup.trim()}
            className="btn-gold inline-flex items-center gap-2 text-sm disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Look up
          </button>
        </form>
        {error && (
          <div
            role="alert"
            className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-900"
          >
            <div className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
              <div className="flex-1">
                <p className="font-medium">Stripe dashboard failed to load</p>
                <p className="mt-1 break-words text-xs text-red-800">{error}</p>
                <ul className="mt-2 list-disc space-y-0.5 ps-4 text-xs text-red-800">
                  <li>Check that the email or user ID is correct.</li>
                  <li>Confirm the Stripe secret key is configured for this environment.</li>
                  <li>Verify your admin session is still active (sign out and back in if needed).</li>
                  <li>If the issue persists, check webhook logs for connectivity errors.</li>
                </ul>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => runLookup()}
                    disabled={busy || !lookup.trim()}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-400 bg-card px-2.5 py-1 text-xs font-medium text-red-900 hover:bg-red-100 dark:border-red-400/40 dark:text-red-200 dark:hover:bg-red-500/15 disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="inline-flex items-center rounded-md px-2.5 py-1 text-xs text-red-800 hover:bg-red-100"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {message && (
          <p className="mt-2 text-xs text-emerald-700">{message}</p>
        )}
      </div>

      {!data ? (
        <p className="text-sm text-muted-foreground">
          Enter an email or user id above to inspect their Stripe + membership state.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* ── Diagnostics (Stripe load debug) ────────────── */}
          {dbg ? (
            <section className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-medium">
                <RefreshCw className="h-4 w-4 text-muted-foreground" />
                Diagnostics
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                    dbg.stripe_mode === "live"
                      ? "bg-red-100 text-red-900"
                      : dbg.stripe_mode === "test"
                        ? "bg-amber-100 text-amber-900"
                        : "bg-muted text-foreground"
                  }`}
                >
                  Stripe {dbg.stripe_mode}
                </span>
                <span className="text-xs text-muted-foreground">
                  {dbg.duration_ms}ms · {dbg.calls.length} calls · {dbg.calls.filter((c) => !c.ok).length} failed
                </span>
              </div>
              {!dbg.stripe_key_configured ? (
                <div className="mb-3 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
                  <strong>Stripe secret key not usable:</strong> {dbg.stripe_key_error ?? "unknown reason"}
                </div>
              ) : null}
              <div className="mb-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                <div>Lookup: <span className="font-mono">{dbg.lookup.by}={dbg.lookup.value}</span></div>
                <div>Resolved user_id: <span className="font-mono">{dbg.resolved_user_id ?? "—"}</span></div>
                <div>Resolved email: <span className="font-mono">{dbg.resolved_email ?? "—"}</span></div>
                <div>Stripe customer: <span className="font-mono">{m?.stripe_customer_id ?? "—"}</span></div>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-border/40">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60 text-start">
                    <tr>
                      <th className="px-2 py-1">Call</th>
                      <th className="px-2 py-1">Resource</th>
                      <th className="px-2 py-1">ms</th>
                      <th className="px-2 py-1">Status</th>
                      <th className="px-2 py-1">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbg.calls.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-3 text-center text-muted-foreground">
                          No calls recorded.
                        </td>
                      </tr>
                    ) : (
                      dbg.calls.map((c, i) => (
                        <tr
                          key={i}
                          className={`border-t border-border/40 ${c.ok ? "" : "bg-red-50"}`}
                        >
                          <td className="px-2 py-1 font-mono">{c.label}</td>
                          <td className="px-2 py-1 font-mono text-muted-foreground">
                            {c.resource ?? "—"}
                          </td>
                          <td className="px-2 py-1">{c.ms}</td>
                          <td className="px-2 py-1">
                            {c.ok ? (
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900">
                                ok
                              </span>
                            ) : (
                              <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-900">
                                fail
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-red-800">
                            {c.error ? (
                              <span className="break-words">
                                {c.error}
                                {c.error_code ? ` [${c.error_code}]` : ""}
                                {c.error_type ? ` (${c.error_type})` : ""}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Full log lines are also written server-side with the prefix
                <span className="mx-1 font-mono">[stripe-ops]</span>
                — search them via server function logs for the same request.
              </p>
            </section>
          ) : null}

          {/* ── At a glance ────────────────────────────────── */}
          <section className="rounded-2xl border-2 border-gold/40 bg-cream/50 p-4 lg:col-span-2">
            <div className="mb-2 text-sm font-medium">At a glance — {m?.email ?? "—"}</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <div className="text-xs text-muted-foreground">Latest charge</div>
                <div className="mt-1 flex items-center gap-2">
                  <StatusPill value={s?.latest_charge?.status ?? null} />
                  {s?.latest_charge ? (
                    <span className="text-sm">
                      {fmtMoney(s.latest_charge.amount, s.latest_charge.currency)}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmtDate(s?.latest_charge?.created ?? null)}
                </div>
                {s?.latest_charge?.failure_message ? (
                  <div className="mt-1 text-xs text-red-700">
                    {s.latest_charge.failure_message}
                  </div>
                ) : null}
                {s?.latest_charge?.dashboard_url ? (
                  <a
                    href={s.latest_charge.dashboard_url}
                    target="_blank"
                    rel="noopener"
                    className="mt-1 inline-flex items-center gap-1 text-xs underline"
                  >
                    Open charge <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Subscription trial_end</div>
                <div className="mt-1 text-sm">
                  {fmtDate(s?.subscription?.trial_end ?? null)}
                </div>
                <div className="text-xs text-muted-foreground">
                  First recurring invoice at trial_end
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Membership active</div>
                <div className="mt-1 flex items-center gap-2">
                  <StatusPill value={m?.status ?? null} />
                  <span className="text-sm">
                    {m?.status === "active" ? "Yes" : "No"}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Sub: <StatusPill value={s?.subscription?.status ?? null} />
                </div>
              </div>
            </div>
          </section>


          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <UserIcon className="h-4 w-4" /> Membership
            </div>
            <Field label="Email" value={m?.email} mono />
            <Field label="User ID" value={<><span className="font-mono">{m?.user_id ?? "—"}</span><CopyBtn value={m?.user_id} /></>} />
            <Field label="Status" value={<StatusPill value={m?.status ?? null} />} />
            <Field label="Tier" value={m?.tier ?? "—"} />
            <Field label="Founding member" value={m?.founding_member ? "Yes" : "No"} />
            <Field label="Registered" value={fmtDate(a?.created_at ?? m?.created_at ?? null)} />
            <Field label="Last login" value={fmtDate(a?.last_sign_in_at)} />
            <Field
              label="Email verified"
              value={a?.email_verified ? "Yes" : "No"}
            />
            <Field label="Activated" value={fmtDate(m?.activated_at)} />
            <Field label="Cancelled" value={fmtDate(m?.cancelled_at)} />
          </section>

          {/* ── Stripe subscription ───────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <CreditCard className="h-4 w-4" /> Stripe
            </div>
            <Field
              label="Customer ID"
              value={
                <>
                  <span className="font-mono">{m?.stripe_customer_id ?? "—"}</span>
                  <CopyBtn value={m?.stripe_customer_id} />
                </>
              }
            />
            <Field
              label="Subscription ID"
              value={
                <>
                  <span className="font-mono">{m?.stripe_subscription_id ?? "—"}</span>
                  <CopyBtn value={m?.stripe_subscription_id} />
                </>
              }
            />
            <Field
              label="Checkout Attempt ID"
              value={
                <>
                  <span className="font-mono">{m?.checkout_attempt_id ?? "—"}</span>
                  <CopyBtn value={m?.checkout_attempt_id} />
                </>
              }
            />
            <Field
              label="Checkout Session"
              value={
                <>
                  <span className="font-mono">{m?.checkout_session_id ?? "—"}</span>
                  <CopyBtn value={m?.checkout_session_id} />
                </>
              }
            />
            <Field label="Subscription status" value={<StatusPill value={s?.subscription?.status ?? null} />} />
            <Field label="Trial end (first recurring charge)" value={fmtDate(s?.subscription?.trial_end ?? null)} />
            <Field label="Current period" value={
              s?.subscription?.current_period_start && s?.subscription?.current_period_end

                ? `${fmtDate(s.subscription.current_period_start)} → ${fmtDate(s.subscription.current_period_end)}`
                : "—"
            } />
            <Field label="Next renewal" value={fmtDate(s?.subscription?.current_period_end ?? null)} />
            <Field label="Cancel at period end" value={s?.subscription?.cancel_at_period_end ? "Yes" : "No"} />
            <Field
              label="Payment method"
              value={
                s?.payment_method
                  ? `${s.payment_method.brand ?? ""} ••••${s.payment_method.last4 ?? "----"} (exp ${s.payment_method.exp_month}/${s.payment_method.exp_year})`
                  : "—"
              }
            />
            <Field label="Last successful payment" value={fmtDate(s?.last_successful_payment ?? null)} />
            <Field label="Last failed payment" value={fmtDate(s?.last_failed_payment ?? null)} />

            <div className="mt-3 flex flex-wrap gap-2">
              {s?.customer?.dashboard_url && (
                <a
                  href={s.customer.dashboard_url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
                >
                  Open customer <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {s?.subscription?.dashboard_url && (
                <a
                  href={s.subscription.dashboard_url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
                >
                  Open subscription <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </section>

          {/* ── Webhook ────────────────────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Webhook className="h-4 w-4" /> Webhook
            </div>
            <Field
              label="Last processed event ID"
              value={
                <>
                  <span className="font-mono">{w?.last_processed_event_id ?? "—"}</span>
                  <CopyBtn value={w?.last_processed_event_id} />
                </>
              }
            />
            <Field label="Last processed at" value={fmtDate(w?.last_processed_event_at)} />
            <Field label="Last webhook received" value={fmtDate(w?.last_received_at)} />
            <Field label="Signature failures (all-time)" value={w?.duplicate_events_count ?? 0} />
            <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-border/40">
              {(!w?.recent_events || w.recent_events.length === 0) ? (
                <p className="p-3 text-xs text-muted-foreground">No webhook events for this customer.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60 text-start">
                    <tr>
                      <th className="px-2 py-1">When</th>
                      <th className="px-2 py-1">Type</th>
                      <th className="px-2 py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w.recent_events.map((e, i) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="px-2 py-1 text-muted-foreground">{fmtDate(e.created_at)}</td>
                        <td className="px-2 py-1 font-mono">{e.event_type}</td>
                        <td className="px-2 py-1">{e.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* ── Webhook errors ─────────────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <ShieldAlert className="h-4 w-4 text-red-600" /> Webhook errors
              {w?.errors && w.errors.length > 0 ? (
                <span className="ms-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-900">
                  {w.errors.length}
                </span>
              ) : null}
              <span className="ms-1 text-xs text-muted-foreground">
                failed handlers or signature errors linked to this member
              </span>
            </div>
            <div className="max-h-80 overflow-y-auto rounded-md border border-border/40">
              {(!w?.errors || w.errors.length === 0) ? (
                <p className="p-3 text-xs text-muted-foreground">
                  No webhook errors recorded for this member. 🎉
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60 text-start">
                    <tr>
                      <th className="px-2 py-1">When</th>
                      <th className="px-2 py-1">Event</th>
                      <th className="px-2 py-1">Error</th>
                      <th className="px-2 py-1">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w.errors.map((e, i) => (
                      <tr key={i} className="border-t border-border/40 align-top">
                        <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">
                          {fmtDate(e.created_at)}
                        </td>
                        <td className="px-2 py-1 font-mono">{e.event_type}</td>
                        <td className="px-2 py-1 text-destructive">
                          {e.error_message ?? e.summary ?? "—"}
                          {e.error_message && e.summary && e.summary !== e.error_message ? (
                            <div className="mt-0.5 text-muted-foreground">{e.summary}</div>
                          ) : null}
                        </td>
                        <td className="px-2 py-1 font-mono text-muted-foreground">
                          {e.reference ? (
                            <>
                              <span className="break-all">{e.reference}</span>
                              <CopyBtn value={e.reference} />
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>



          {/* ── Charge timeline ────────────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <CreditCard className="h-4 w-4" /> Charge timeline
              {s?.recent_charges && s.recent_charges.length > 0 ? (
                <span className="ms-1 text-xs text-muted-foreground">
                  ({s.recent_charges.length} most recent)
                </span>
              ) : null}
            </div>
            <div className="max-h-96 overflow-y-auto rounded-md border border-border/40">
              {(!s?.recent_charges || s.recent_charges.length === 0) ? (
                <p className="p-3 text-xs text-muted-foreground">
                  No charges on this customer yet.
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60 text-start">
                    <tr>
                      <th className="px-2 py-1">When</th>
                      <th className="px-2 py-1">Amount</th>
                      <th className="px-2 py-1">Status</th>
                      <th className="px-2 py-1">Card</th>
                      <th className="px-2 py-1">Detail</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.recent_charges.map((c) => {
                      const displayStatus =
                        c.refunded && c.amount_refunded >= c.amount
                          ? "refunded"
                          : c.amount_refunded > 0
                            ? "partially_refunded"
                            : c.status;
                      return (
                        <tr key={c.id} className="border-t border-border/40 align-top">
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">
                            {fmtDate(c.created)}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            {fmtMoney(c.amount, c.currency)}
                            {c.amount_refunded > 0 && (
                              <div className="text-[10px] text-muted-foreground">
                                −{fmtMoney(c.amount_refunded, c.currency)} refunded
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <StatusPill value={displayStatus} />
                          </td>
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">
                            {c.card_brand || c.card_last4
                              ? `${c.card_brand ?? "card"} ···· ${c.card_last4 ?? "----"}`
                              : "—"}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {c.failure_message ? (
                              <span className="text-destructive">
                                {c.failure_code ? `${c.failure_code}: ` : ""}
                                {c.failure_message}
                              </span>
                            ) : (
                              c.description ?? "—"
                            )}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            <a
                              href={c.dashboard_url}
                              target="_blank"
                              rel="noopener"
                              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                              title="Open in Stripe"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                            {c.receipt_url && (
                              <a
                                href={c.receipt_url}
                                target="_blank"
                                rel="noopener"
                                className="ms-2 inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                                title="Receipt"
                              >
                                <Receipt className="h-3 w-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* ── Subscription event log ────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4" /> Subscription event log
              <span className="ms-1 text-xs text-muted-foreground">
                trial_end, cancellations, payment outcomes
              </span>
            </div>
            {(() => {
              type LogRow = {
                at: number;
                kind: string;
                label: string;
                detail: string | null;
                tone: "ok" | "warn" | "err" | "info";
                href: string | null;
              };
              const rows: LogRow[] = [];

              // 1. Webhook events for this customer/subscription/session
              const subRe = /^(customer\.subscription\.|invoice\.(paid|payment_failed|payment_succeeded|marked_uncollectible|voided)|charge\.(succeeded|failed|refunded|refund)|payment_intent\.(succeeded|payment_failed))/;
              for (const e of w?.recent_events ?? []) {
                if (!subRe.test(e.event_type)) continue;
                const tone: LogRow["tone"] =
                  e.status === "error"
                    ? "err"
                    : e.event_type.includes("failed") || e.event_type.includes("uncollectible")
                      ? "err"
                      : e.event_type.includes("deleted") || e.event_type.includes("refund") || e.event_type.includes("cancel")
                        ? "warn"
                        : "ok";
                rows.push({
                  at: new Date(e.created_at).getTime() / 1000,
                  kind: e.event_type,
                  label: e.event_type,
                  detail: e.summary ?? e.reference ?? null,
                  tone,
                  href: e.event_id
                    ? `https://dashboard.stripe.com/events/${e.event_id}`
                    : null,
                });
              }

              // 2. Charge outcomes (payment outcomes source-of-truth pre-launch)
              for (const c of s?.recent_charges ?? []) {
                const isRefund =
                  c.refunded || c.amount_refunded > 0;
                const label = isRefund
                  ? c.amount_refunded >= c.amount
                    ? "charge.refunded"
                    : "charge.partially_refunded"
                  : c.status === "succeeded"
                    ? "charge.succeeded"
                    : c.status === "failed"
                      ? "charge.failed"
                      : `charge.${c.status}`;
                const tone: LogRow["tone"] =
                  c.status === "failed"
                    ? "err"
                    : isRefund
                      ? "warn"
                      : "ok";
                const detail = c.failure_message
                  ? `${c.failure_code ? c.failure_code + ": " : ""}${c.failure_message}`
                  : `${fmtMoney(c.amount, c.currency)}${
                      c.amount_refunded > 0
                        ? ` (−${fmtMoney(c.amount_refunded, c.currency)} refunded)`
                        : ""
                    }`;
                rows.push({
                  at: c.created,
                  kind: label,
                  label,
                  detail,
                  tone,
                  href: c.dashboard_url,
                });
              }

              // 3. Current subscription state anchors (trial_end, cancellation)
              if (s?.subscription?.trial_end) {
                rows.push({
                  at: s.subscription.trial_end,
                  kind: "trial_end",
                  label: "trial_end (first recurring charge)",
                  detail: `Subscription ${s.subscription.id} — status ${s.subscription.status}`,
                  tone: "info",
                  href: s.subscription.dashboard_url,
                });
              }
              if (s?.subscription?.canceled_at) {
                rows.push({
                  at: s.subscription.canceled_at,
                  kind: "subscription.canceled",
                  label: s.subscription.cancel_at_period_end
                    ? "cancel scheduled at period end"
                    : "subscription canceled",
                  detail: s.subscription.current_period_end
                    ? `Access until ${fmtDate(s.subscription.current_period_end)}`
                    : null,
                  tone: "warn",
                  href: s.subscription.dashboard_url,
                });
              }

              rows.sort((a, b) => b.at - a.at);

              if (rows.length === 0) {
                return (
                  <p className="p-2 text-xs text-muted-foreground">
                    No subscription events recorded for this customer yet.
                  </p>
                );
              }

              const toneClass: Record<LogRow["tone"], string> = {
                ok: "bg-emerald-100 text-emerald-900",
                warn: "bg-amber-100 text-amber-900",
                err: "bg-red-100 text-red-900",
                info: "bg-sky-100 text-sky-900",
              };

              return (
                <div className="max-h-96 overflow-y-auto rounded-md border border-border/40">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted/60 text-start">
                      <tr>
                        <th className="px-2 py-1">When</th>
                        <th className="px-2 py-1">Event</th>
                        <th className="px-2 py-1">Detail</th>
                        <th className="px-2 py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="border-t border-border/40 align-top">
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">
                            {fmtDate(r.at)}
                          </td>
                          <td className="px-2 py-1">
                            <span
                              className={`rounded-full px-2 py-0.5 font-mono ${toneClass[r.tone]}`}
                            >
                              {r.label}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {r.detail ?? "—"}
                          </td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            {r.href && (
                              <a
                                href={r.href}
                                target="_blank"
                                rel="noopener"
                                className="inline-flex text-muted-foreground hover:text-foreground"
                                title="Open in Stripe"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </section>



          {/* ── Invoices ──────────────────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Receipt className="h-4 w-4" /> Invoices
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border border-border/40">
              {(!s?.invoices || s.invoices.length === 0) ? (
                <p className="p-3 text-xs text-muted-foreground">No invoices.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/60 text-start">
                    <tr>
                      <th className="px-2 py-1">When</th>
                      <th className="px-2 py-1">Amount</th>
                      <th className="px-2 py-1">Status</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.invoices.map((i) => (
                      <tr key={i.id} className="border-t border-border/40">
                        <td className="px-2 py-1 text-muted-foreground">{fmtDate(i.created)}</td>
                        <td className="px-2 py-1">{fmtMoney(i.amount_paid || i.amount_due, i.currency)}</td>
                        <td className="px-2 py-1"><StatusPill value={i.status} /></td>
                        <td className="px-2 py-1">
                          {i.hosted_invoice_url && (
                            <a href={i.hosted_invoice_url} target="_blank" rel="noopener" className="inline-flex text-muted-foreground hover:text-foreground">
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {/* ── Timeline ──────────────────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4" /> Audit timeline
            </div>
            {(!data.timeline || data.timeline.length === 0) ? (
              <p className="p-2 text-xs text-muted-foreground">Nothing to show yet.</p>
            ) : (
              <ol className="relative ms-2 border-s border-border/60 ps-4">
                {data.timeline.map((t, i) => (
                  <li key={i} className="mb-3 last:mb-0">
                    <span className="absolute -start-1.5 mt-1 h-3 w-3 rounded-full bg-primary/70" />
                    <div className="text-xs text-muted-foreground">{fmtDate(t.at)}</div>
                    <div className="text-sm">{t.label}</div>
                    {t.details && (
                      <div className="text-xs text-muted-foreground">{t.details}</div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {/* ── Recovery actions ──────────────────────────── */}
          <section className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <RefreshCw className="h-4 w-4" /> Recovery actions
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                disabled={!m?.user_id || busy}
                onClick={async () => {
                  if (!m?.user_id) return;
                  setBusy(true); setError(null); setMessage(null);
                  try {
                    const res = await resync({ data: { user_id: m.user_id } });
                    setMessage(
                      res.ok
                        ? `Resynced → status: ${res.status ?? "(unchanged)"}`
                        : `Resync skipped: ${res.reason ?? "unknown"}`,
                    );
                    await runLookup(m.email ?? m.user_id);
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally { setBusy(false); }
                }}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs disabled:opacity-40"
              >
                <RefreshCw className="h-3 w-3" /> Refresh from Stripe
              </button>
              <button
                disabled={!m?.email || busy}
                onClick={async () => {
                  if (!m?.email) return;
                  setBusy(true); setError(null); setMessage(null);
                  try {
                    const res = await createCheckout({ data: { target_email: m.email } });
                    if (res.ok === false) {
                      setMessage(res.message);
                    } else if (res.url) {
                      window.open(res.url, "_blank", "noopener");
                      setMessage("New checkout session opened.");
                    }
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally { setBusy(false); }
                }}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Generate new checkout
              </button>
              <button
                disabled={!m?.stripe_customer_id || !m?.user_id || busy}
                onClick={async () => {
                  if (!m?.user_id) return;
                  setBusy(true); setError(null); setMessage(null);
                  try {
                    const res = await portal({ data: { user_id: m.user_id } });
                    if (res.url) window.open(res.url, "_blank", "noopener");
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally { setBusy(false); }
                }}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Open billing portal
              </button>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Read-only diagnostics. "Refresh from Stripe" pulls the live subscription state and
              writes it back through the same code path the webhook uses (per-subscription ordering
              guards still apply).
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
